<template>
    <BModal ref="modal" :title="title" centered no-close-on-backdrop size="lg" header-class="modal-title" modal-class="no-border-modal" @hidden="onHidden">
        <div v-if="licenseNeeded" class="alert alert-danger mb-0">{{ $t('installationRooms.licenseNeeded') }}</div>
        <form v-else ref="formRef" novalidate @submit.prevent="submit">
            <div class="mb-3">
                <label for="room-label" class="form-label">{{ $t('installationRooms.label') }}</label>
                <input id="room-label" ref="labelInput" type="text" class="form-control" v-model.trim="label" maxlength="64"
                    :placeholder="$t('installationRooms.labelPlaceholder')" autocomplete="off" required />
            </div>
            <label class="form-label mb-0">{{ $t('installationRooms.readers') }}</label>
            <p class="text-muted small mb-2">{{ $t('installationRooms.readersHelp') }}</p>
            <div v-if="hubs.length === 0" class="alert alert-warning mb-0">{{ $t('installationRooms.noReader') }}</div>
            <div v-else class="room-readers border rounded p-2">
                <div v-for="hub in hubs" :key="hub.hub_id" class="mb-2">
                    <div class="d-flex align-items-center justify-content-between">
                        <span class="fw-bold text-primary"><i class="mdi mdi-server-network me-1"></i>{{ hub.hub_label }}</span>
                        <button type="button" class="btn btn-link btn-sm p-0" @click="toggleHub(hub)">
                            {{ allSelected(hub) ? $t('installationRooms.unselectAll') : $t('installationRooms.selectAll') }}
                        </button>
                    </div>
                    <div v-for="reader in hub.readers" :key="reader.id" class="form-check ms-1">
                        <input class="form-check-input" type="checkbox" :id="`room-reader-${reader.id}`" :value="reader.id" v-model="selected" />
                        <label class="form-check-label" :for="`room-reader-${reader.id}`">{{ reader.reader_label }}</label>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="d-flex justify-content-between align-items-center w-100">
                <button type="button" class="btn btn-secondary" @click="close(null)">{{ $t(licenseNeeded ? 'common.close' : 'common.cancel') }}</button>
                <button v-if="!licenseNeeded" type="button" class="btn btn-primary ms-2" @click="submit">{{ $t('common.save') }}</button>
            </div>
        </template>
    </BModal>
</template>

<script>
import { BModal } from 'bootstrap-vue-next';
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// A room to create or edit: its label and the readers it is made of, picked
// among the installation's readers grouped by hub / LINK, possibly none (the
// room is then only booked, nothing opens it). Each reader shows its own
// name, as in the components tree: a door may have two (entry and exit).
// A new room also takes a place in the installation's HAXO BOOKING licenses:
// without one (licenseNeeded), the modal only says so, nothing to fill nor
// save, as the backend would refuse the room (402).
// show(room, readersByHub, licenseNeeded) resolves with { label, reader_ids }, or null when cancelled.
export default {
    name: 'RoomModal',
    components: {
        BModal
    },
    setup() {
        const { t } = useI18n();
        const modal = ref(null);
        const formRef = ref(null);
        const labelInput = ref(null);
        const title = ref('');
        const label = ref('');
        const hubs = ref([]);
        const selected = ref([]);
        const licenseNeeded = ref(false);
        let resolver = null;

        const show = (room, readersByHub, needsLicense = false) => {
            title.value = room.id ? t('installationRooms.editRoom') : t('installationRooms.addRoom');
            label.value = room.label ?? '';
            hubs.value = readersByHub;
            selected.value = room.readers.map(reader => reader.id);
            licenseNeeded.value = needsLicense;
            modal.value.show();
            return new Promise(resolve => { resolver = resolve; });
        };

        const close = (result) => {
            const resolve = resolver;
            resolver = null;
            modal.value.hide();
            if (resolve) resolve(result);
        };

        const onHidden = () => {
            if (resolver) close(null);
        };

        const allSelected = (hub) => hub.readers.every(reader => selected.value.includes(reader.id));

        const toggleHub = (hub) => {
            const ids = hub.readers.map(reader => reader.id);
            if (allSelected(hub)) {
                selected.value = selected.value.filter(id => !ids.includes(id));
            } else {
                selected.value = [...new Set([...selected.value, ...ids])];
            }
        };

        const submit = async () => {
            await nextTick();
            if (!formRef.value.checkValidity()) {
                formRef.value.reportValidity();
                return;
            }
            close({ label: label.value, reader_ids: [...selected.value] });
        };

        return {
            modal,
            formRef,
            labelInput,
            title,
            label,
            hubs,
            selected,
            licenseNeeded,
            show,
            close,
            onHidden,
            allSelected,
            toggleHub,
            submit,
        };
    }
}
</script>

<style scoped>
.room-readers {
    max-height: 50vh;
    overflow-y: auto;
}
</style>
